import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, IReportState, IInvoiceCreditPaymentsResponse, ReportActionCreators } from '@nkr/store/index';
import { IInvoiceCreditPaymentsReport } from '@nkr/models/index';
import { Button, FormControl, Label, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { ReportWrapper } from '../missed-match-offers/styles';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, CsvExportParams, FirstDataRenderedEvent, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, ProcessCellForExportParams, RowDataChangedEvent, ValueGetterParams } from 'ag-grid-community';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { OrganType } from '@nkr/enums/organ-type';
import { Utility } from '@nkr/services/utility';

interface IOwnProps { }

interface IStateProps {
    reports: IReportState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getInvoiceCreditPaymentsReport: (centerId: number, startDate: string, endDate: string, organType: OrganType) => Promise<IInvoiceCreditPaymentsResponse>,
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: IInvoiceCreditPaymentsReport[],
    centerId: number,
    organTypes: OrganType[],
    gridApi: GridApi,
    startDate: string,
    endDate: string,
    columns: ColDef[]
}

class InvoiceCreditPaymentsReport extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        centerId: 0,
        organTypes: [OrganType.Kidney, OrganType.Liver],
        gridApi: null,
        startDate: moment().add(-1, 'years').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        columns: [
            {
                colId: 'rank',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
                headerName: 'R#',
                width: 40,
                valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1,
                sortable: false
            },
            {
                colId: 'centerAlias',
                field: 'centerAlias',
                headerName: 'Center',
                comparator: Utility.stringSortComparator,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
                width: 155
            },
            {
                colId: 'organType',
                field: 'organType',
                headerName: 'Organ Type',
                headerComponentFramework: (params: IHeaderParams) =>
                    <SortableHeaderComponent {...params}>Organ Type</SortableHeaderComponent>,
                width: 150,
                cellRendererFramework: (params: ICellRendererParams) => {
                    let data = params.data as IInvoiceCreditPaymentsReport;
                    let organType = data.organType == null ? '--' : data.organType == 1 ? 'L' : 'K';
                    return <Label bsStyle={data.organType == null ? 'default' : data.organType == 1 ? 'warning' : 'danger'} style={{ fontSize: '100%' }}>{organType}</Label>;
                }
            },
            {
                colId: 'invoiceNumber',
                field: 'invoiceNumber',
                headerName: 'Invoice #',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Invoice #</SortableHeaderComponent>,
                width: 105
            },

            {
                colId: 'invoiceYear',
                field: 'invoiceYear',
                headerName: 'Invoice Year',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Invoice<br />Year</SortableHeaderComponent>,
                width: 85
            },

            {
                colId: 'invoiceMonth',
                field: 'invoiceMonth',
                headerName: 'Invoice Month',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Invoice<br />Month</SortableHeaderComponent>,
                width: 85
            },

            {
                colId: 'invoiceAmount',
                field: 'invoiceAmount',
                headerName: 'Total Invoice Amount',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total Invoice<br />Amount</SortableHeaderComponent>,
                width: 105,
                cellRenderer: (props: ICellRendererParams) => props.value !== null ? `\$${props.value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : "0"
            },

            {
                colId: 'amountPaid',
                field: 'amountPaid',
                headerName: 'Total Amount Paid/Credited',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total Amount<br />Paid/Credited</SortableHeaderComponent>,
                width: 105,
                cellRenderer: (props: ICellRendererParams) => props.value !== null ? `\$${props.value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : ""
            },

            {
                colId: 'amountRemaining',
                field: 'amountRemaining',
                headerName: 'Total Amount Remaining',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Total Amount<br />Remaining</SortableHeaderComponent>,
                width: 105,
                cellRenderer: (props: ICellRendererParams) => props.value !== null ? `\$${props.value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : "0"
            },

            {
                colId: 'paymentDate',
                field: 'paymentDate',
                headerName: 'Payment/Credit Date',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Payment/<br />Credit Date</SortableHeaderComponent>,
                width: 105,
                cellRenderer: (props: ICellRendererParams) => props.value != null ? moment(props.value).format('MM/DD/YYYY') : ''
            },

            {
                colId: 'paymentType',
                field: 'paymentType',
                headerName: 'Payment or Credit',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Payment or<br />Credit</SortableHeaderComponent>,
                width: 105
            },

            {
                colId: 'paymentAmount',
                field: 'paymentAmount',
                headerName: 'Payment Amount',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Payment<br />Amount</SortableHeaderComponent>,
                width: 105,
                cellRenderer: (props: ICellRendererParams) => props.value !== null ? `\$${props.value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : ""
            },

            {
                colId: 'invoiceDueDate',
                field: 'invoiceDueDate',
                headerName: 'Invoice Due Date',
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Invoice Due<br />Date</SortableHeaderComponent>,
                width: 105,
                valueGetter: (props: ValueGetterParams) => props.data != null ? moment({ year: props.data.invoiceYear, month: props.data.invoiceMonth - 1, day: 1 }).endOf('month').add('days', 45).format('MM/DD/YYYY') : '',
                comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                    let dateA = moment(valueA, 'MM/DD/YYYY');
                    let dateB = moment(valueB, 'MM/DD/YYYY');

                    if (dateA.isBefore(dateB)) {
                        return isDescending ? 1 : -1;
                    } else if (dateA.isAfter(dateB)) {
                        return isDescending ? -1 : 1;
                    } else {
                        return 0;
                    }
                }
            },

            {
                colId: 'numberOfDaysToPay',
                headerName: 'numberOfDaysToPay',
                width: 60,
                headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}># of Days<br />to Pay</SortableHeaderComponent>,
                cellRenderer: (props: ICellRendererParams) => {
                    return props.value < 0 ? "--" : props.value.toString();
                },
                valueGetter: (props: ValueGetterParams) => {
                    if (props.data != null) {
                        let invoiceDate = moment({ year: props.data.invoiceYear, month: props.data.invoiceMonth - 1, day: 1 }).endOf('month').startOf('day');
                        return props.data.paymentDate == null ? -1 : moment(props.data.paymentDate).diff(invoiceDate, 'days');
                    }
                    else {
                        return -1;
                    }
                }
            },
        ]
    };

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });

        e.api.sizeColumnsToFit();
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let { startDate, endDate, organTypes } = this.state;
        let organType = organTypes.length == 2 ? null : organTypes[0];

        this.props.showLoading('Loading report data, one moment please...').then(() => {
            this.props.getInvoiceCreditPaymentsReport(this.state.centerId, startDate, endDate, organType).then((response) => {
                if (response.data.success) {
                    let list = response.data.results;

                    this.setState({
                        data: list
                    }, () => {
                        this.props.hideLoading();
                    });
                } else {
                    this.props.hideLoading();
                }
            })
        });
    }

    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerId: parseInt(e.currentTarget.value)
        });
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (name == 'startDate') {
            this.setState({
                startDate: value
            });
        }

        if (name == 'endDate') {
            this.setState({
                endDate: value
            });
        }
    }

    changeOrganType = (values: OrganType | OrganType[]) => {
        let safeValues = Array.isArray(values) ? values : [values];
        if (safeValues.length == 0) return;
        this.setState({ organTypes: safeValues }, () => this.getData());
    }

    handleSearch = () => {
        this.getData();
    }

    handleExport = () => {
        let filePrefix = this.state.centerId > 0 ? this.state.data.filter(x => x.centerId == this.state.centerId).map(x => x.centerAlias)[0].toLowerCase() : 'allcenters';

        let params: CsvExportParams = {
            columnGroups: true,
            fileName: filePrefix + '-invoice-credits-and-payments.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.field == 'organType')
                    return params.value == 0 ? 'Kidney' : params.value == 1 ? 'Liver' : params.value;

                if (colDef.colId == 'paymentDate')
                    return params.value == null ? '' : moment(params.value).format('MM/DD/YYYY');

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        const centerList = _.sortBy(_.uniqBy(this.state.data.map(x => ({ id: x.centerId, alias: x.centerAlias })), item => item.id), item => item.alias);
        const data = this.state.centerId > 0 ? this.state.data.filter(x => x.centerId == this.state.centerId) : this.state.data;
        const organValue = this.state.organTypes.length == 2 ? -1 : this.state.organTypes[0];

        return (
            <>
                <div style={{ fontSize: 28, fontWeight: 500, marginBottom: 20 }}>
                    Invoice Credits and Payments Report
                </div>

                <div className='well well-sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', fontSize: 14, flexWrap: 'wrap', gap: 10 }}>

                    <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>

                        <div className='form-group' style={{ marginBottom: 0 }}>
                            <label className='control-label' style={{ display: 'block', marginBottom: 4 }}>Center</label>

                            <FormControl
                                componentClass='select'
                                value={this.state.centerId}
                                onChange={this.handleCenterChange}
                            >
                                <option value={0}>- All Centers -</option>
                                {centerList.map(center =>
                                    <option key={`ctr-${center.id}`} value={center.id}>{center.alias}</option>
                                )}

                            </FormControl>
                        </div>
                        <div className='form-group' style={{ marginBottom: 0 }}>
                            <label className='control-label' style={{ display: 'block', marginBottom: 4 }}>Invoice Start</label>
                            <FormControl

                                type='date'
                                name='startDate'
                                value={this.state.startDate}
                                onChange={this.handleDateChange}
                            />

                        </div>
                        <div className='form-group' style={{ marginBottom: 0 }}>
                            <label className='control-label' style={{ display: 'block', marginBottom: 4 }}>Invoice End</label>
                            <FormControl

                                type='date'
                                name='endDate'
                                value={this.state.endDate}
                                onChange={this.handleDateChange}
                            />

                        </div>
                        <Button bsStyle='primary' onClick={this.handleSearch} style={{ marginLeft: 30 }}>
                            Date Filter
                        </Button>
                        <div className='form-group' style={{ marginBottom: 0, marginLeft: 50 }}>
                            <label className='control-label' style={{ display: 'block', marginBottom: 4 }}>Organ:</label>
                            <ToggleButtonGroup type='radio' name='organs' value={organValue}>
                                <ToggleButton value={-1} onChange={() => this.changeOrganType([OrganType.Kidney, OrganType.Liver])}
                                    className={this.state.organTypes.length == 2 ? 'btn-primary btn-md' : 'btn-md'}>All</ToggleButton>
                                <ToggleButton value={OrganType.Kidney} onChange={() => this.changeOrganType(OrganType.Kidney)}
                                    className={this.state.organTypes.length == 1 && this.state.organTypes.includes(OrganType.Kidney) ? 'btn-primary btn-md' : 'btn-md'}>Kidney</ToggleButton>
                                <ToggleButton value={OrganType.Liver} onChange={() => this.changeOrganType(OrganType.Liver)}
                                    className={this.state.organTypes.length == 1 && this.state.organTypes.includes(OrganType.Liver) ? 'btn-primary btn-md' : 'btn-md'}>Liver</ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                    </div>
                    <Button bsStyle='success' onClick={this.handleExport}>Export CSV</Button>
                </div>

                <ReportWrapper className='ag-theme-bootstrap' style={{ height: '80vh' }}>
                    <AgGridReact
                        defaultColDef={{
                            headerClass: 'list-header',
                            cellClass: 'list-cell',
                            sortable: true,
                            resizable: false
                        }}
                        columnDefs={this.state.columns}
                        rowData={data}
                        headerHeight={46}
                        groupHeaderHeight={30}
                        rowHeight={30}
                        suppressRowClickSelection={false}
                        suppressCellSelection={true}
                        noRowsOverlayComponentFramework={() => <div>No payment/credits found...</div>}
                        onGridReady={this.onGridReady}
                        onFirstDataRendered={this.onFirstDataRendered}
                        onRowDataChanged={this.onRowDataChanged}
                    />
                </ReportWrapper>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getInvoiceCreditPaymentsReport: (centerId: number, startDate: string, endDate: string, organType: OrganType) => {
            return dispatch(ReportActionCreators.getInvoiceCreditPaymentsReport(centerId, startDate, endDate, organType));
        },
    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(InvoiceCreditPaymentsReport);